import { useState } from 'react';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis,
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, ReferenceLine,
} from 'recharts';
import { useJobPoller } from '../hooks/useJobPoller';
import type { DiagnosisReport, Finding, Recommendation } from '../types';
import './ReportViewer.css';

type Severity = DiagnosisReport['overall_severity'];

const SEV_RANK: Record<Severity, number> = {
  critical: 5, high: 4, medium: 3, low: 2, informational: 1,
};
const SEV_LABEL: Record<Severity, string> = {
  critical: 'Critical', high: 'High', medium: 'Medium',
  low: 'Low', informational: 'Info',
};
const SEV_COLOR: Record<Severity, string> = {
  critical: '#e05050', high: '#e07830',
  medium: '#e8a135', low: '#3a8fd4', informational: '#8b8f99',
};
const ZONE_COLOR: Record<string, string> = {
  A: '#3dc47e', B: '#e8a135', C: '#e07830', D: '#e05050', unknown: '#8b8f99',
};

function SeverityBadge({ sev, large }: { sev: Severity; large?: boolean }) {
  return (
    <span
      className={`sev-badge ${large ? 'sev-badge--lg' : ''}`}
      style={{ background: `${SEV_COLOR[sev]}22`, color: SEV_COLOR[sev] }}
    >
      {SEV_LABEL[sev]}
    </span>
  );
}

function ConfidenceBar({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  const color = pct >= 75 ? '#3dc47e' : pct >= 50 ? '#e8a135' : '#e05050';
  return (
    <div className="conf-bar-wrap">
      <div className="conf-bar">
        <div style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="conf-pct">{pct}%</span>
    </div>
  );
}

// ── Bearing Radar Chart ────────────────────────────────────────────────────

function BearingRadar({ indicators }: { indicators: Record<string, number | null> }) {
  const thresholds: Record<string, number> = {
    Kurtosis: 10, 'Crest Factor': 12, 'Impulse Factor': 12,
    'Clearance Factor': 15, RMS: 1, Skewness: 3,
  };
  const data = [
    { metric: 'Kurtosis', value: indicators.kurtosis ?? 0, threshold: 4 },
    { metric: 'Crest Factor', value: indicators.crest_factor ?? 0, threshold: 6 },
    { metric: 'Impulse Factor', value: indicators.impulse_factor ?? 0, threshold: 8 },
    { metric: 'Clearance Factor', value: indicators.clearance_factor ?? 0, threshold: 10 },
    { metric: 'Skewness', value: Math.abs(indicators.skewness ?? 0), threshold: 1 },
  ].map(d => ({
    ...d,
    normalised: Math.min((d.value / (thresholds[d.metric] ?? 10)) * 100, 100),
    threshNorm: Math.min((d.threshold / (thresholds[d.metric] ?? 10)) * 100, 100),
  }));

  return (
    <div className="chart-card">
      <h3 className="chart-title">Bearing health indicators <span className="iso-tag">ISO 13373-2</span></h3>
      <ResponsiveContainer width="100%" height={220}>
        <RadarChart data={data}>
          <PolarGrid stroke="#2a2d33" />
          <PolarAngleAxis dataKey="metric" tick={{ fill: '#8b8f99', fontSize: 10 }} />
          <Radar name="Threshold" dataKey="threshNorm" stroke="#e8a13555" fill="#e8a13511" strokeDasharray="4 2" />
          <Radar name="Measured" dataKey="normalised" stroke="#3a8fd4" fill="#3a8fd422" strokeWidth={2} />
        </RadarChart>
      </ResponsiveContainer>
      <p className="chart-note">Dashed = fault threshold. Filled = measured value.</p>
    </div>
  );
}

// ── Frequency Spectrum ─────────────────────────────────────────────────────

function FrequencyChart({ peaks }: { peaks: { hz: number; magnitude: number }[] }) {
  const data = [...peaks]
    .sort((a, b) => a.hz - b.hz)
    .map(p => ({ name: `${Math.round(p.hz)}Hz`, magnitude: +p.magnitude.toFixed(3) }));

  return (
    <div className="chart-card">
      <h3 className="chart-title">Dominant frequency peaks <span className="iso-tag">ISO 13373-1</span></h3>
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={data} margin={{ top: 4, right: 8, bottom: 20, left: 8 }}>
          <XAxis dataKey="name" tick={{ fill: '#8b8f99', fontSize: 9 }} angle={-35} textAnchor="end" />
          <YAxis tick={{ fill: '#8b8f99', fontSize: 10 }} />
          <Tooltip
            contentStyle={{ background: '#1c1f23', border: '1px solid #2a2d33', borderRadius: 6 }}
            labelStyle={{ color: '#eceef2' }}
            itemStyle={{ color: '#3a8fd4' }}
          />
          <Bar dataKey="magnitude" fill="#3a8fd4" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Optical Flow Timeline ──────────────────────────────────────────────────

function FlowTimeline({ data }: { data: number[] }) {
  const mean = data.reduce((a, b) => a + b, 0) / data.length;
  const chartData = data.map((v, i) => ({ t: `${i}s`, flow: +v.toFixed(3) }));

  return (
    <div className="chart-card">
      <h3 className="chart-title">Optical flow timeline <span className="iso-tag">ISO 13373-1</span></h3>
      <ResponsiveContainer width="100%" height={160}>
        <LineChart data={chartData} margin={{ top: 4, right: 8, bottom: 4, left: 8 }}>
          <XAxis dataKey="t" tick={{ fill: '#8b8f99', fontSize: 9 }} interval={Math.floor(data.length / 6)} />
          <YAxis tick={{ fill: '#8b8f99', fontSize: 10 }} />
          <Tooltip
            contentStyle={{ background: '#1c1f23', border: '1px solid #2a2d33', borderRadius: 6 }}
            labelStyle={{ color: '#eceef2' }}
            itemStyle={{ color: '#3dc47e' }}
          />
          <ReferenceLine y={mean} stroke="#e8a135" strokeDasharray="4 2" label={{ value: 'mean', fill: '#e8a135', fontSize: 9 }} />
          <Line type="monotone" dataKey="flow" stroke="#3dc47e" dot={false} strokeWidth={1.5} />
        </LineChart>
      </ResponsiveContainer>
      <p className="chart-note">Amber dashed = mean magnitude. Spikes indicate motion events.</p>
    </div>
  );
}

// ── Risk Matrix ────────────────────────────────────────────────────────────

function RiskMatrix({ findings }: { findings: Finding[] }) {

  return (
    <div className="chart-card">
      <h3 className="chart-title">Fault risk matrix <span className="iso-tag">ISO 13379-1</span></h3>
      <div className="risk-matrix">
        {(['critical', 'high', 'medium', 'low', 'informational'] as Severity[]).map(sev => {
          const matching = findings.filter(f => f.severity === sev);
          return (
            <div key={sev} className="risk-row">
              <SeverityBadge sev={sev} />
              <div className="risk-items">
                {matching.length === 0 ? (
                  <span className="risk-none">—</span>
                ) : (
                  matching.map((f, i) => (
                    <div key={i} className="risk-item">
                      <span className="risk-fault">{f.fault_type}</span>
                      <span className="risk-conf">{Math.round(f.confidence * 100)}%</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── ISO Zone Badge ─────────────────────────────────────────────────────────

function ISOZoneBadge({ zone }: { zone?: string }) {
  if (!zone || zone === 'unknown') return null;
  const color = ZONE_COLOR[zone] ?? '#8b8f99';
  const labels: Record<string, string> = {
    A: 'Zone A — New machine', B: 'Zone B — Acceptable',
    C: 'Zone C — Alarm', D: 'Zone D — Danger',
  };
  return (
    <div className="iso-zone" style={{ borderColor: color, color }}>
      <span className="iso-zone-label">ISO 10816-3</span>
      <span className="iso-zone-value">{labels[zone] ?? zone}</span>
    </div>
  );
}

// ── Finding accordion ──────────────────────────────────────────────────────

function FindingRow({ f, index }: { f: Finding & { iso_reference?: string }; index: number }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <div className={`finding-row ${open ? 'finding-row--open' : ''}`}>
      <button className="finding-header" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <div className="finding-header-left">
          <SeverityBadge sev={f.severity} />
          <span className="finding-type">{f.fault_type}</span>
          {f.iso_reference && <span className="iso-ref">{f.iso_reference}</span>}
        </div>
        <div className="finding-header-right">
          <ConfidenceBar value={f.confidence} />
          <span className={`chevron ${open ? 'chevron--open' : ''}`}>›</span>
        </div>
      </button>
      {open && (
        <div className="finding-body">
          <p className="finding-desc">{f.description}</p>
          {f.evidence.length > 0 && (
            <div className="evidence-list">
              <span className="evidence-label">Evidence</span>
              <ul>{f.evidence.map((e, i) => <li key={i}>{e}</li>)}</ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Recommendation row ─────────────────────────────────────────────────────

function RecRow({ r, index }: { r: Recommendation; index: number }) {
  return (
    <div className="rec-row">
      <div className="rec-index">{String(index + 1).padStart(2, '0')}</div>
      <div className="rec-body">
        <div className="rec-header">
          <span className="rec-action">{r.action}</span>
          <SeverityBadge sev={r.priority} />
        </div>
        <div className="rec-meta">
          <span className="rec-tag">⏱ {r.timeframe}</span>
          {r.estimated_downtime_hours != null && (
            <span className="rec-tag">🔧 {r.estimated_downtime_hours}h downtime</span>
          )}
        </div>
        {r.parts_likely_needed.length > 0 && (
          <div className="rec-parts">
            <span className="rec-parts-label">Parts:</span>
            {r.parts_likely_needed.map((p, i) => (
              <span key={i} className="rec-part-chip">{p}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Full report dashboard ──────────────────────────────────────────────────

function FullReport({ report, onNew }: { report: DiagnosisReport & { iso_condition_zone?: string; optical_flow_timeline?: number[]; frequency_peaks?: { hz: number; magnitude: number }[]; bearing_indicators?: Record<string, number | null> }; onNew: () => void }) {
  const sortedFindings = [...report.findings].sort(
    (a, b) => SEV_RANK[b.severity] - SEV_RANK[a.severity]
  );
  const generatedAt = new Date(report.generated_at).toLocaleString('en-SG', {
    dateStyle: 'medium', timeStyle: 'short',
  });

  const hasAudio = !!report.bearing_indicators?.kurtosis;
  const hasVideo = !!report.optical_flow_timeline?.length || !!report.frequency_peaks?.length;

  return (
    <div className="report">
      {/* Header */}
      <div className="report-header">
        <div className="report-header-left">
          <div className="wordmark">AxiomSensa</div>
          <span className="report-timestamp">{generatedAt}</span>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={onNew}>+ New diagnosis</button>
      </div>

      {/* Verdict strip */}
      <div className={`verdict verdict--${report.overall_severity}`}>
        <div className="verdict-left">
          <div className="verdict-label">Overall verdict</div>
          <SeverityBadge sev={report.overall_severity} large />
        </div>
        <div className="verdict-right">
          <div className="verdict-label">Confidence</div>
          <div className="verdict-conf">{Math.round(report.overall_confidence * 100)}%</div>
        </div>
        {report.iso_condition_zone && (
          <div className="verdict-zone">
            <ISOZoneBadge zone={report.iso_condition_zone} />
          </div>
        )}
        <p className="verdict-summary">{report.summary}</p>
      </div>

      {/* Dashboard grid */}
      <div className="dashboard-grid">

        {/* Left column — findings + recommendations */}
        <div className="dashboard-left">
          {sortedFindings.length > 0 && (
            <section className="report-section">
              <h2 className="section-title">
                <span>Fault findings</span>
                <span className="section-count">{sortedFindings.length}</span>
              </h2>
              <div className="findings-list">
                {sortedFindings.map((f, i) => (
                  <FindingRow key={i} f={f as any} index={i} />
                ))}
              </div>
            </section>
          )}

          {report.recommendations.length > 0 && (
            <section className="report-section">
              <h2 className="section-title">
                <span>Maintenance recommendations</span>
                <span className="section-count">{report.recommendations.length}</span>
              </h2>
              <div className="rec-list">
                {report.recommendations.map((r, i) => (
                  <RecRow key={i} r={r} index={i} />
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Right column — charts */}
        <div className="dashboard-right">
          <RiskMatrix findings={sortedFindings} />

          {hasAudio && report.bearing_indicators && (
            <BearingRadar indicators={report.bearing_indicators} />
          )}

          {report.frequency_peaks && report.frequency_peaks.length > 0 && (
            <FrequencyChart peaks={report.frequency_peaks} />
          )}

          {report.optical_flow_timeline && report.optical_flow_timeline.length > 1 && (
            <FlowTimeline data={report.optical_flow_timeline} />
          )}

          {!hasAudio && !hasVideo && (
            <div className="chart-card chart-card--empty">
              <p>Upload audio for spectral charts or video for optical flow timeline.</p>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="report-footer">
        <span>Job <code>{report.job_id}</code></span>
        <span>ISO 13373 · ISO 13379 · ISO 10816-3 · ISO 17359</span>
        <span>{generatedAt}</span>
      </div>
    </div>
  );
}

// ── Waiting screen ─────────────────────────────────────────────────────────

function WaitingScreen({ elapsed }: { elapsed: number }) {
  const stages = [
    { label: 'Downloading media', threshold: 0 },
    { label: 'Running spectral analysis', threshold: 8 },
    { label: 'Computing optical flow', threshold: 20 },
    { label: 'Extracting keyframes', threshold: 40 },
    { label: 'Consulting Claude vision', threshold: 55 },
    { label: 'Assembling report', threshold: 85 },
  ];
  const activeIdx = stages.reduce((acc, s, i) => (elapsed >= s.threshold ? i : acc), 0);

  return (
    <div className="waiting">
      <div className="waiting-spinner" aria-label="Processing" />
      <h2 className="waiting-title">Analysis in progress</h2>
      <p className="waiting-elapsed">{elapsed}s elapsed</p>
      <ul className="waiting-stages" aria-live="polite">
        {stages.map((s, i) => (
          <li key={i} className={i < activeIdx ? 'stage--done' : i === activeIdx ? 'stage--active' : 'stage--pending'}>
            <span className="stage-dot" />
            {s.label}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Main export ────────────────────────────────────────────────────────────

export function ReportViewer({ jobId, onNew }: { jobId: string; onNew: () => void }) {
  const { status, report, error, elapsedSeconds } = useJobPoller(jobId);

  if (status === 'idle' || status === 'processing') {
    return <WaitingScreen elapsed={elapsedSeconds} />;
  }

  if (status === 'failed') {
    return (
      <div className="report-error">
        <div className="error-icon">⚠</div>
        <h2>Analysis failed</h2>
        <p>{error}</p>
        <button className="btn btn-primary" onClick={onNew}>Try again</button>
      </div>
    );
  }

  if (!report) return null;
  return <FullReport report={report as any} onNew={onNew} />;
}
